import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  defaultSettings,
  demoUsers,
  generateAttendance,
  generateStudents,
  schoolClasses,
} from "../data/mockData";
import { todayISO } from "../lib/utils";
import type {
  AppSettings,
  AttendanceMethod,
  AttendanceRecord,
  AttendanceStatus,
  DemoUser,
  Role,
  ScanResult,
  SchoolClass,
  Student,
} from "../types";

const STORAGE = {
  session: "josephcare.session",
  students: "josephcare.students",
  attendance: "josephcare.attendance",
  settings: "josephcare.settings",
};

const safeLoad = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const seedStudents = generateStudents();
const seedAttendance = generateAttendance(seedStudents);

interface AppContextValue {
  session: DemoUser | null;
  students: Student[];
  attendance: AttendanceRecord[];
  classes: SchoolClass[];
  settings: AppSettings;
  login: (role: Role) => void;
  logout: () => void;
  addStudent: (student: Omit<Student, "id" | "code" | "qrPayload" | "initials">) => Student;
  updateStudent: (student: Student) => void;
  markAttendance: (
    codeOrPayload: string,
    status?: AttendanceStatus,
    method?: AttendanceMethod,
  ) => ScanResult;
  updateAttendance: (recordId: string, status: AttendanceStatus) => void;
  saveSettings: (settings: AppSettings) => void;
  resetDemo: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<DemoUser | null>(() =>
    safeLoad<DemoUser | null>(STORAGE.session, null),
  );
  const [students, setStudents] = useState<Student[]>(() =>
    safeLoad<Student[]>(STORAGE.students, seedStudents),
  );
  const [attendance, setAttendance] = useState<AttendanceRecord[]>(() =>
    safeLoad<AttendanceRecord[]>(STORAGE.attendance, seedAttendance),
  );
  const [settings, setSettings] = useState<AppSettings>(() =>
    safeLoad<AppSettings>(STORAGE.settings, defaultSettings),
  );

  useEffect(() => localStorage.setItem(STORAGE.session, JSON.stringify(session)), [session]);
  useEffect(() => localStorage.setItem(STORAGE.students, JSON.stringify(students)), [students]);
  useEffect(
    () => localStorage.setItem(STORAGE.attendance, JSON.stringify(attendance)),
    [attendance],
  );
  useEffect(() => localStorage.setItem(STORAGE.settings, JSON.stringify(settings)), [settings]);

  const login = useCallback((role: Role) => setSession(demoUsers[role]), []);
  const logout = useCallback(() => setSession(null), []);

  const addStudent = useCallback(
    (draft: Omit<Student, "id" | "code" | "qrPayload" | "initials">) => {
      const nextNumber =
        Math.max(
          0,
          ...students.map((student) => Number(student.code.split("-").at(-1)) || 0),
        ) + 1;
      const code = `STJ-${new Date().getFullYear()}-${String(nextNumber).padStart(4, "0")}`;
      const created: Student = {
        ...draft,
        id: `student-${String(nextNumber).padStart(4, "0")}`,
        code,
        qrPayload: `JOSEPHCARE|${code}|v1`,
        initials: draft.name
          .split(/\s+/)
          .filter((part) => !["bin", "binti"].includes(part.toLowerCase()))
          .slice(0, 2)
          .map((part) => part[0])
          .join("")
          .toUpperCase(),
      };
      setStudents((current) => [created, ...current]);
      return created;
    },
    [students],
  );

  const updateStudent = useCallback((updated: Student) => {
    setStudents((current) =>
      current.map((student) => (student.id === updated.id ? updated : student)),
    );
  }, []);

  const markAttendance = useCallback(
    (
      codeOrPayload: string,
      status: AttendanceStatus = "present",
      method: AttendanceMethod = "qr",
    ): ScanResult => {
      const normalized = codeOrPayload.trim();
      const code = normalized.includes("|") ? normalized.split("|")[1] : normalized;
      const student = students.find(
        (item) => item.code.toLowerCase() === code?.toLowerCase(),
      );
      if (!student) {
        return {
          ok: false,
          kind: "not_found",
          message: "Kod QR tidak dikenali. Semak kod atau cuba murid lain.",
        };
      }
      if (!student.active) {
        return {
          ok: false,
          kind: "inactive",
          student,
          message: "Profil murid ini tidak aktif. Hubungi pentadbir.",
        };
      }
      const today = todayISO();
      const duplicate = attendance.find(
        (record) => record.studentId === student.id && record.date === today,
      );
      if (duplicate) {
        return {
          ok: false,
          kind: "duplicate",
          student,
          record: duplicate,
          message: `${student.name} sudah direkodkan hari ini.`,
        };
      }
      const record: AttendanceRecord = {
        id: `attendance-${today}-${student.id}`,
        studentId: student.id,
        date: today,
        timestamp: new Date().toISOString(),
        status,
        method,
        recordedBy: session?.name ?? "Pengguna demo",
      };
      setAttendance((current) => [record, ...current]);
      return {
        ok: true,
        kind: "success",
        student,
        record,
        message: `Kehadiran ${student.name} berjaya direkodkan.`,
      };
    },
    [attendance, session?.name, students],
  );

  const updateAttendance = useCallback((recordId: string, status: AttendanceStatus) => {
    setAttendance((current) =>
      current.map((record) => (record.id === recordId ? { ...record, status } : record)),
    );
  }, []);

  const saveSettings = useCallback((nextSettings: AppSettings) => {
    setSettings(nextSettings);
  }, []);

  const resetDemo = useCallback(() => {
    const nextStudents = generateStudents();
    setStudents(nextStudents);
    setAttendance(generateAttendance(nextStudents));
    setSettings(defaultSettings);
    setSession(null);
    Object.values(STORAGE).forEach((key) => localStorage.removeItem(key));
  }, []);

  const value = useMemo<AppContextValue>(
    () => ({
      session,
      students,
      attendance,
      classes: schoolClasses,
      settings,
      login,
      logout,
      addStudent,
      updateStudent,
      markAttendance,
      updateAttendance,
      saveSettings,
      resetDemo,
    }),
    [
      session,
      students,
      attendance,
      settings,
      login,
      logout,
      addStudent,
      updateStudent,
      markAttendance,
      updateAttendance,
      saveSettings,
      resetDemo,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
};

